<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { storeToRefs } from "pinia"
import { useAuthStore } from "@core/stores/auth.store"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"
import type { WorkspaceBreadcrumbModelItem } from "@modules/home/components/dashboard/workspace/HomeWorkspaceBreadcrumbItem.vue"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Mes dossiers",
            breadcrumb: {
                root: "Mon espace",
            },
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const homeWorkspace = useHomeWorkspaceStore()
const { breadcrumb } = storeToRefs(homeWorkspace)

// ================================
// COMPUTED
// ================================
const getFolderId = computed(() => (route.params.folderId as string) || null)

/**
 * Fil d’Ariane dossiers (racine + segments API) avec cibles de dépôt DnD (`dropTargetFolderId`).
 */
const getWorkspaceBreadcrumbModel = computed<WorkspaceBreadcrumbModelItem[]>(
    () => {
        const crumbs = breadcrumb.value
        if (!crumbs.length) {
            return [
                {
                    label: t("breadcrumb.root"),
                    icon: "pi pi-home",
                    route: "/folder",
                    dropTargetFolderId: null,
                },
            ]
        }
        return [
            {
                label: t("breadcrumb.root"),
                icon: "pi pi-home",
                route: "/folder",
                dropTargetFolderId: null,
            },
            ...crumbs.map((crumb) => ({
                label: crumb.name ?? "",
                icon: "pi pi-folder",
                route: `/folder/${crumb._id}`,
                dropTargetFolderId: crumb._id ?? null,
            })),
        ]
    }
)

/** Titre de la section bâtiments : racine vs dossier courant. */
const getBuildingSectionTitle = computed(() =>
    getFolderId.value ? "Bâtiments" : "Bâtiments sans dossiers"
)

// ================================
// INTERACTIONS
// ================================
function onOpenExport(): void {
    homeWorkspace.openModal("exportAllData", {})
}

function onOpenBuildingOptions(): void {
    homeWorkspace.openModal("buildingOptions", {})
}

// ================================
// WATCHERS & LIFECYCLE
// ================================
watch(
    getFolderId,
    (id) => {
        homeWorkspace.currentFolderId = id
    },
    { immediate: true }
)

onMounted(() => {
    if (!authStore.isClientHomeys) {
        router.replace({ path: "/building" })
    }
})

watch(
    () => authStore.isClientHomeys,
    (v) => {
        if (!v) router.replace({ path: "/building" })
    }
)
</script>

<template>
    <!-- ================================ -->
    <!-- PAGE — MES DOSSIERS -->
    <!-- ================================ -->
    <main
        v-if="authStore.isClientHomeys"
        class="page page-dashboard-folder flex min-h-0 flex-1 flex-col gap-12 p-6"
        data-cy="page-dashboard-folder"
    >
        <HomeWorkspaceDnDProvider>
            <!-- <HeaderPage :title="getBuildingSectionTitle" /> -->
            <section
                class="flex flex-col gap-6"
                data-cy="dashboard-folder-section"
            >
                <HeaderPage
                    :title="t('title')"
                    class-header="flex justify-between items-center flex-wrap gap-4"
                >
                    <template #breadcrumb>
                        <HomeWorkspaceBreadcrumb
                            :model="getWorkspaceBreadcrumbModel"
                            :disabled-drop="!authStore.isEmployeWriteOrHigher"
                        />
                    </template>
                    <div class="flex flex-wrap items-center gap-2">
                        <BaseButton
                            v-if="authStore.isEmployeWriteOrHigher"
                            label="Exporter les données"
                            severity="secondary"
                            icon="pi pi-upload"
                            icon-pos="right"
                            @click="onOpenExport"
                        />
                        <BaseButton
                            v-if="authStore.isEmployeWriteOrHigher"
                            label="Ajouter des bâtiments"
                            icon="pi pi-plus"
                            icon-pos="right"
                            @click="onOpenBuildingOptions"
                        />
                    </div>
                </HeaderPage>
                <HomeFolderList />
            </section>

            <section
                class="flex flex-col gap-6"
                data-cy="dashboard-folder-buildings-section"
            >
                <HeaderPage
                    :title="getBuildingSectionTitle"
                    class-header="flex justify-between items-center flex-wrap gap-4"
                >
                </HeaderPage>

                <HomeBuildingList variant="folder" :folder-id="getFolderId" />
            </section>

            <HomeBuildingDialogs />
            <HomeFolderDialogs />
            <HomeExportDialogs />
            <HomeBuildingActionsDialog />
        </HomeWorkspaceDnDProvider>
    </main>
</template>
